import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Search, CheckCircle, XCircle, Loader2, FileText, MapPin, Phone } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AGOSPage = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [noResults, setNoResults] = useState(false);
  const debounceRef = useRef(null);
  const wrapperRef = useRef(null);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Live search with debounce
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!searchQuery || searchQuery.length < 2) {
      setSuggestions([]);
      setShowSuggestions(false);
      setNoResults(false);
      return;
    }
    debounceRef.current = setTimeout(async () => {
      setSearching(true);
      setNoResults(false);
      try {
        const res = await axios.get(`${API}/gos-members/search?q=${encodeURIComponent(searchQuery.trim())}`);
        setSuggestions(res.data);
        setShowSuggestions(true);
        if (res.data.length === 0) setNoResults(true);
      } catch {
        setSuggestions([]);
      } finally {
        setSearching(false);
      }
    }, 300);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [searchQuery]);

  const selectMember = (member) => {
    setSelectedMember(member);
    setShowSuggestions(false);
    setSearchQuery(member.name);
  };

  const clearSearch = () => {
    setSearchQuery('');
    setSelectedMember(null);
    setSuggestions([]);
    setNoResults(false);
  };

  return (
    <div className="min-h-screen pt-32 pb-16" data-testid="agos-page">
      <div className="max-w-4xl mx-auto px-6 md:px-12">
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-6xl font-bold mb-4" data-testid="agos-title">
            All Gujarat <span className="gradient-text">Ophthalmological Society</span>
          </h1>
          <p className="text-lg text-slate-300">Check your GOS Membership Number or Apply for Membership</p>
        </div>

        {/* Member Lookup */}
        <div className="glass-card p-8 md:p-12 mb-8 neon-glow" data-testid="member-lookup">
          <h2 className="text-2xl font-semibold mb-6 flex items-center gap-3">
            <Search className="h-6 w-6 text-[#FF6F4A]" />
            Check Your GOS Membership Number
          </h2>
          <p className="text-slate-400 text-sm mb-6">Search by your name, membership number, or mobile number. Suggestions will appear as you type.</p>

          <div className="relative" ref={wrapperRef}>
            <div className="flex gap-3">
              <div className="relative flex-1">
                <Input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => { setSearchQuery(e.target.value); setSelectedMember(null); }}
                  onFocus={() => { if (suggestions.length > 0) setShowSuggestions(true); }}
                  className="glass-card border-white/20 text-white"
                  placeholder="Type name, membership number, or mobile..."
                  data-testid="member-search-input"
                />
                {searching && <Loader2 className="absolute right-3 top-3 h-4 w-4 animate-spin text-[#FF6F4A]" />}
              </div>
              {searchQuery && (
                <Button onClick={clearSearch} variant="outline" className="glass-card text-slate-300 border-white/20 px-4">
                  Clear
                </Button>
              )}
            </div>

            {/* Suggestions Dropdown */}
            {showSuggestions && suggestions.length > 0 && (
              <div className="absolute z-50 w-full mt-2 glass-card border border-white/20 rounded-xl max-h-80 overflow-y-auto" data-testid="suggestions-dropdown">
                {suggestions.map((member, idx) => (
                  <button
                    key={idx}
                    onClick={() => selectMember(member)}
                    className="w-full text-left px-4 py-3 hover:bg-white/10 transition-colors border-b border-white/5 last:border-0 flex items-center gap-3"
                    data-testid={`suggestion-${idx}`}
                  >
                    <CheckCircle className="h-4 w-4 text-green-400 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-white font-medium text-sm truncate">{member.name}</p>
                      <div className="flex gap-3 text-xs">
                        <span className="text-[#FF6F4A] font-mono">No: {member.membership_no}</span>
                        {member.mobile && <span className="text-slate-400">{member.mobile}</span>}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}

            {/* No Results */}
            {showSuggestions && noResults && searchQuery.length >= 2 && (
              <div className="absolute z-50 w-full mt-2 glass-card border border-yellow-500/30 rounded-xl p-4 text-center">
                <XCircle className="h-6 w-6 text-yellow-400 mx-auto mb-2" />
                <p className="text-sm text-yellow-300">No member found. See below for how to apply.</p>
              </div>
            )}
          </div>

          {/* Selected Member Card */}
          {selectedMember && (
            <div className="mt-6 glass-card p-6 border-2 border-green-500/30 bg-green-500/5" data-testid="selected-member">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-green-500/20 flex items-center justify-center flex-shrink-0">
                  <CheckCircle className="h-6 w-6 text-green-400" />
                </div>
                <div>
                  <p className="text-white font-bold text-lg">{selectedMember.name}</p>
                  <div className="flex flex-wrap gap-4 mt-1">
                    <span className="text-[#FF6F4A] font-mono font-bold text-lg">GOS No: {selectedMember.membership_no}</span>
                    {selectedMember.email && <span className="text-sm text-slate-400">{selectedMember.email}</span>}
                    {selectedMember.mobile && <span className="text-sm text-slate-400">{selectedMember.mobile}</span>}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Membership Info */}
        <div className="glass-card p-8 md:p-12 mb-8" data-testid="membership-info">
          <h2 className="text-2xl font-semibold mb-6 flex items-center gap-3">
            <FileText className="h-6 w-6 text-[#FFB6C1]" />
            AGOS Life Membership
          </h2>

          <div className="space-y-6">
            <div className="glass-card p-6 border-[#FF6F4A]/30">
              <p className="text-slate-300 leading-relaxed mb-4">
                Those who are not members of Gujarat Ophthalmological Society and have applied or are planning to apply for membership can register for the conference at GOS Member rates. Simply select <strong className="text-[#FF6F4A]">&quot;Applied for GOS Membership&quot;</strong> on the registration page.
              </p>
              <p className="text-slate-300 leading-relaxed mb-4">
                Applied members are eligible to participate in <strong className="text-white">Paper and Poster Presentations</strong> and attend all scientific sessions of the conference.
              </p>
              <div className="glass-card p-4 border-yellow-500/30 bg-yellow-500/5 mt-4">
                <p className="text-yellow-300 text-sm font-semibold mb-1">Important Note on Voting Rights:</p>
                <p className="text-slate-300 text-sm">Voting rights at the AGOS General Body Meeting are reserved exclusively for verified GOS Life Members. New membership applications require verification of physical documents once submitted, which may take some time. Voting privileges will be granted only after your membership has been fully ratified by the society.</p>
              </div>
            </div>

            <div className="glass-card p-6">
              <h3 className="text-xl font-semibold text-[#FF6F4A] mb-4">How to Apply for AGOS Life Membership</h3>
              <ol className="space-y-3 text-slate-300">
                <li className="flex gap-3">
                  <span className="text-[#FF6F4A] font-bold">1.</span>
                  <span>Fill the AGOS Life Membership form</span>
                </li>
                <li className="flex gap-3">
                  <span className="text-[#FF6F4A] font-bold">2.</span>
                  <span>Attach your <strong className="text-white">Degree &amp; Registration Certificate</strong></span>
                </li>
                <li className="flex gap-3">
                  <span className="text-[#FF6F4A] font-bold">3.</span>
                  <span>Two <strong className="text-white">passport size photos</strong></span>
                </li>
                <li className="flex gap-3">
                  <span className="text-[#FF6F4A] font-bold">4.</span>
                  <span>A cheque of <strong className="text-white">Rs. 5,900</strong> in favour of <strong className="text-[#FF6F4A]">All Gujarat Ophthalmological Society</strong></span>
                </li>
              </ol>
            </div>

            <div className="glass-card p-6 border-[#FFB6C1]/30">
              <h3 className="text-xl font-semibold text-[#FFB6C1] mb-4 flex items-center gap-2">
                <MapPin className="h-5 w-5" />
                Send Documents To
              </h3>
              <div className="text-slate-300 leading-relaxed">
                <p className="font-semibold text-white text-lg mb-2">Jyoti Eye Hospital</p>
                <p className="font-semibold text-white">Dr. Bhavik Zala</p>
                <p className="mt-3">
                  A Block, 3rd Floor<br />
                  305-306-307<br />
                  Arjun Iconic<br />
                  Behind Rannapark Bus Stand<br />
                  Opposite Satya Two Complex<br />
                  Naranpura - Rannapark<br />
                  Ahmedabad 380013<br />
                  Gujarat, India
                </p>
                <div className="mt-4 flex items-center gap-2">
                  <Phone className="h-4 w-4 text-[#FF6F4A]" />
                  <span>For Appointment: <a href="tel:+919727717182" className="text-[#FF6F4A] font-semibold hover:text-[#FFB6C1]">97 27 71 71 82</a></span>
                </div>
              </div>
            </div>

            <div className="glass-card p-6">
              <h3 className="text-lg font-semibold text-white mb-3">Download Membership Form</h3>
              <p className="text-sm text-slate-400 mb-4">Download the AGOS Life Membership application form, fill it out, and send it along with the required documents.</p>
              <a
                href="https://customer-assets.emergentagent.com/job_agos2026/artifacts/49f1j6w9_WhatsApp%20Image%202026-06-30%20at%202.12.50%20PM.jpeg"
                download="AGOS_Life_Membership_Form.jpeg"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-[#FF6F4A] hover:bg-[#FF6F4A]/90 text-white font-semibold px-6 py-3 rounded-lg transition-colors"
                data-testid="download-form-button"
              >
                <FileText className="h-4 w-4" />
                Download Membership Form
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AGOSPage;
